import React from "react";
import styled, { css, DefaultTheme } from "styled-components";

// `surface` is CRM's neutral header button: a bordered, unfilled control that
// turns accent on hover — what its grid uses for Add, Export and the like.
type Variant = "contained" | "outlined" | "text" | "surface";
type Color = "primary" | "secondary" | "error";
type Size = "small" | "medium" | "large";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  color?: Color;
  size?: Size;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  component?: React.ElementType;
  // CRM's icon-button shape: a square, bordered control carrying one icomoon glyph.
  iconOnly?: boolean;
  preIconClassName?: string;
  // MUI-only props, accepted and ignored so consumers keep type-checking:
  disableRipple?: boolean;
  disableFocusRipple?: boolean;
  disableElevation?: boolean;
};

const Button = ({
  variant = "contained",
  color = "primary",
  size = "medium",
  fullWidth = false,
  startIcon,
  endIcon,
  children,
  type = "button",
  component,
  iconOnly = false,
  preIconClassName,
  disableRipple,
  disableFocusRipple,
  disableElevation,
  ...rest
}: ButtonProps) => (
  <StyledButton
    as={component}
    $variant={variant}
    $color={color}
    $size={size}
    $fullWidth={fullWidth}
    $iconOnly={iconOnly}
    type={type}
    {...rest}
  >
    {preIconClassName && <Icon className={preIconClassName} />}
    {startIcon && <span className="btn-icon">{startIcon}</span>}
    {children}
    {endIcon && <span className="btn-icon">{endIcon}</span>}
  </StyledButton>
);

export default Button;

const mainColor = (theme: DefaultTheme, color: Color) => {
  switch (color) {
    case "error":
      return theme.colors.btn.danger;
    case "secondary":
      return theme.colors.fg.secondary;
    default:
      return theme.colors.fg.accent;
  }
};

const sizeStyles: Record<Size, ReturnType<typeof css>> = {
  small: css`
    height: 28px;
    padding: 0 12px;
    ${({ theme }) => theme.typography.smallText4};
  `,
  medium: css`
    height: 36px;
    padding: 0 16px;
    ${({ theme }) => theme.typography.buttonsText};
    line-height: 22px;
  `,
  large: css`
    height: 42px;
    padding: 0 20px;
    ${({ theme }) => theme.typography.subtitle4};
  `,
};

export const Icon = styled.i`
  font-size: 24px;
  color: ${({ theme }) => theme.colors.fg.tertiary};
`;

// Shared by the `surface` variant and by every icon-only button, whatever
// variant it was given.
const surfaceStyles = css`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.bg.divider};
  background: ${({ theme }) => theme.colors.bg.primary};
  color: ${({ theme }) => theme.colors.fg.secondary};

  &:hover:not(:disabled) {
    opacity: 1;
    color: ${({ theme }) => theme.colors.fg.accent};
    border-color: ${({ theme }) => theme.colors.fg.accent};

    ${Icon} {
      color: ${({ theme }) => theme.colors.fg.accent};
    }
  }
`;

const StyledButton = styled.button<{
  $variant: Variant;
  $color: Color;
  $size: Size;
  $fullWidth: boolean;
  $iconOnly: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  /* CRM rounds every button to 8px (components/button/Button). */
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
  ${({ $size }) => sizeStyles[$size]}
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}

  .btn-icon {
    display: inline-flex;
    align-items: center;
  }

  ${({ theme, $variant, $color }) => {
    const c = mainColor(theme, $color);
    if ($variant === "surface") {
      return surfaceStyles;
    }
    if ($variant === "outlined") {
      return css`
        background: transparent;
        color: ${c};
        border: 1px solid ${c};
        &:hover {
          background: ${theme.colors.fg.accent12};
        }
      `;
    }
    if ($variant === "text") {
      return css`
        background: transparent;
        color: ${c};
        border: none;
        &:hover {
          background: ${theme.colors.fg.accent12};
        }
      `;
    }
    return css`
      background: ${c};
      color: ${theme.colors.txt.secondary};
      border: 1px solid ${c};
      &:hover {
        opacity: 0.9;
      }
    `;
  }}

  /* CRM's icon button is always the bordered surface shape, whatever the
     variant, so this overrides the fill applied above. */
  ${({ $iconOnly }) =>
    $iconOnly &&
    css`
      min-width: 36px;
      height: 36px;
      padding: 0;
      ${surfaceStyles}
    `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
